// Third-party
import React from 'react';
import moment from 'moment';

// Components
import DatesDisclaimer from './DatesDisclaimer';

// Utils
import { formatMessageWithLocale, FormattedMessage } from '../../../../utils/i18n';
import { getRollingDisclaimerDates } from '../../../../utils/disclaimers/comparison';
import { getLatestGraphedDate } from '../../../../utils/charts';
import ComparisonItem from '@/types/comparison-item';
import RollingIntervals from '@/types/rolling-intervals';
import Locale from '@/types/locale';

interface Props {
  items: ComparisonItem[];
  series: number[][];
  interval: RollingIntervals;
  endDate?: Date;
  shorthand?: boolean;
  showItemNames?: boolean;
  locale?: Locale;
  startAndEndDisclaimerDates?: {
    startDate: Date | null;
    endDate: Date | null;
  };
}

const DrawdownDisclaimer = ({ items, series, interval, endDate, shorthand, showItemNames, locale, startAndEndDisclaimerDates }: Props) => {
  if (shorthand && !startAndEndDisclaimerDates) {
    const latestDate = new Date(getLatestGraphedDate(series));
    const date = moment(latestDate).utc().format(formatMessageWithLocale('Common.DateFormats.Short', locale));

    return (
      <span className="disclaimer dates-disclaimer">
        <FormattedMessage id={`Disclaimers.ShortDrawdown.${interval.toUpperCase()}`} values={{ date }} locale={locale} />
      </span>
    );
  }

  if (startAndEndDisclaimerDates && startAndEndDisclaimerDates.startDate && startAndEndDisclaimerDates.endDate) {
    return (
      <span className="disclaimer dates-disclaimer">
        <FormattedMessage
          id={`Disclaimers.ShortDrawdownWithStartAndEnd.${interval.toUpperCase()}`}
          values={{
            startDate: moment(startAndEndDisclaimerDates.startDate).utc().format(formatMessageWithLocale('Common.DateFormats.Short', locale)),
            endDate: moment(startAndEndDisclaimerDates.endDate).utc().format(formatMessageWithLocale('Common.DateFormats.Short', locale)),
          }}
          locale={locale}
        />
      </span>
    );
  }

  return (
    <DatesDisclaimer
      globalPrefix={formatMessageWithLocale(`Disclaimers.Drawdown.${interval.toUpperCase()}`, locale)}
      itemPrefix={formatMessageWithLocale('Disclaimers.EndingOn', locale)}
      itemDates={getRollingDisclaimerDates(
        items.filter(item => item.holdings.length > 0),
        interval,
        endDate,
        locale,
      )}
      showItemNames={showItemNames}
      locale={locale}
    />
  );
};

export default DrawdownDisclaimer;
